import { Manifest } from "aws-sdk/clients/importexport";
import { BobrequestPipelineFactory } from "../domain/factories/bobrequest-pipeline-factory";
import { ICacheProvider } from "../domain/interfaces/cache-provider.interface";
import { IErrorReporter } from "../domain/interfaces/error-reporter.interface";
import { ITracker } from "../domain/interfaces/tracker.interface";
import { HtmlRepository } from "../domain/repositories/html-repository";
import { ManifestRepository } from "../domain/repositories/manifest-repository";
import { BOBEnvironment } from "../express/bob-environment";
import { DomainPolicy } from "../express/domain-policy";
import { ErrorMiddleware } from "../express/error-middleware";
import { Passthrough } from "../express/passthrough";
import { BobServer } from "../express/server";
import { Cache } from "../infrastructure/cache/cache";
import { CachingPolicy } from "../infrastructure/cache/caching-policy";
import { NullCacheProvider } from "../infrastructure/cache/null-cache-provider";
import { DI } from "../infrastructure/di";
import { NullErrorReporter } from "../infrastructure/trackers/null-error-reporter";
import { NullTracker } from "../infrastructure/trackers/null-tracker";
import { RequestDispatcher } from "../infrastructure/request-dispatcher";

export class BobBootstrapper {

	public configureEnvironment(): void {
		DI.container.bind(DI.DOMAIN_POLICY).to(DomainPolicy);
		DI.container.bind(DI.ENVIRONMENT).toConstantValue(BOBEnvironment.getEnvironment());
		DI.container.bind(DI.BOB_REQUEST_PIPELINE_FACTORY).to(BobrequestPipelineFactory);
		DI.container.bind(DI.BOB_PORT).toConstantValue(BOBEnvironment.getPort());
		DI.container.bind(DI.BOB_SERVER).to(BobServer);
		DI.container.bind(DI.CACHING_POLICY).to(CachingPolicy);
		DI.container.bind(DI.HTML_REPOSITORY).to(HtmlRepository);
		DI.container.bind(DI.MANIFEST_REPOSITORY).to(ManifestRepository);
		DI.container.bind(DI.PASSTHROUGH).to(Passthrough);
		DI.container.bind(DI.ERROR_MIDDLEWARE).to(ErrorMiddleware);

		// Infrastructure
		DI.container.bind(DI.REQUEST_DISPATCHER).to(RequestDispatcher);
		DI.container.bind(DI.CACHE).to(Cache);

		this.setIfUnbind(DI.CACHE_PROVIDER, new NullCacheProvider());
		this.setIfUnbind(DI.TRACKER, new NullTracker());
		this.setIfUnbind(DI.ERROR_REPORTER, new NullErrorReporter());
	}

	public setCacheProvider(cacheProvider: ICacheProvider) {
		DI.container.bind(DI.CACHE_PROVIDER).toConstantValue(cacheProvider);
	}

	public setTracker(tracker: ITracker) {
		DI.container.bind(DI.TRACKER).toConstantValue(tracker);
	}

	public setErrorReporter(errorReporter: IErrorReporter) {
		DI.container.bind(DI.ERROR_REPORTER).toConstantValue(errorReporter);
	}

	public setPort(port: string){
		process.env.BOB_PORT = port;
	}

	public setOriginProtocol(protocol: string){
		process.env.BOB_ORIGIN_PROTOCOL = protocol;
	}

	public setOriginPort(port: string){
		process.env.BOB_ORIGIN_PORT = port;
	}

	public setIgnoreSSL(bool: boolean){
		process.env.BOB_IGNORE_SSL = bool ? 'true' : 'false';
	}

	public setValidDomains(domains: string[]){
		process.env.BOB_VALID_DOMAINS = domains.join(';');
	}

	public setValidTLDs(tlds: string[]){
		process.env.BOB_VALID_TOP_LEVEL_DOMAINS = tlds.join(';');
	}

	public setOriginDomain(domain: string){
		process.env.BOB_FALLBACK_DOMAIN = domain;
		process.env.BOB_FORCE_FALLBACK_DOMAIN = 'true';
	}

	public start() {
		this.configureEnvironment();

		const server: BobServer = DI.container.get(DI.BOB_SERVER);
		server.initialize();
	}

	private setIfUnbind(symbol: symbol, value) {
		if (!DI.container.isBound(symbol)) {
			DI.container.bind(symbol).toConstantValue(value);
		}
	}
}
